import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Title,
  Text,
  Button,
  Card,
  Group,
  Stack,
  Badge,
  Avatar,
  ThemeIcon,
  Select,
  Switch,
  Tabs,
  Table,
  ActionIcon,
  Modal,
  TextInput,
  Textarea,
  Loader,
  Center,
  Alert,
  Paper,
  Box,
  Divider,
  ScrollArea,
  Breadcrumbs,
  Anchor
} from '@mantine/core';
import { personaPrivacyAPI, friendsAPI } from '../services/api';
import { useSubscription } from '../contexts/SubscriptionContext';

const PersonaPrivacyPage = () => {
  const navigate = useNavigate();
  const { isProUser } = useSubscription();

  // Privacy settings state
  const [privacySettings, setPrivacySettings] = useState({
    privacy_mode: 'private',
    trusted_circle_mode: false,
    suggestions_this_month: 0,
    suggestions_limit: 10,
    is_pro_user: false
  });
  const [settingsLoading, setSettingsLoading] = useState(true);

  // Trusted circle state
  const [trustedCircle, setTrustedCircle] = useState([]);
  const [trustedCircleLoading, setTrustedCircleLoading] = useState(false);

  // Per-friend overrides state
  const [overrides, setOverrides] = useState([]);
  const [overridesLoading, setOverridesLoading] = useState(false);

  // Suggestions state
  const [receivedSuggestions, setReceivedSuggestions] = useState([]);
  const [sentSuggestions, setSentSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);

  // Modal states
  const [showAddTrusted, setShowAddTrusted] = useState(false);
  const [showOverrideModal, setShowOverrideModal] = useState(false);
  const [showSuggestionModal, setShowSuggestionModal] = useState(false);
  const [selectedFriend, setSelectedFriend] = useState(null);
  const [selectedPersona, setSelectedPersona] = useState(null);

  // Form states
  const [friends, setFriends] = useState([]);
  const [overrideForm, setOverrideForm] = useState({
    can_suggest: true,
    is_trusted: false,
    override_mode: null
  });
  const [suggestionForm, setSuggestionForm] = useState({
    category: 'Technology',
    tag: '',
    weight: 0,
    reasoning: ''
  });

  const categories = [
    'Technology', 'Food', 'Music', 'Sports', 'Books',
    'Movies', 'Games', 'Fashion', 'Travel', 'Pets',
    'Art', 'Cooking', 'Fitness', 'Photography'
  ];

  useEffect(() => {
    loadPrivacyData();
  }, []);

  const loadPrivacyData = async () => {
    try {
      setSettingsLoading(true);
      const settingsResponse = await personaPrivacyAPI.getSettings();
      setPrivacySettings(settingsResponse.data);

      // Load Pro features if user is Pro
      if (settingsResponse.data.is_pro_user) {
        await Promise.all([
          loadTrustedCircle(),
          loadOverrides(),
          loadSuggestions()
        ]);
      } else {
        await loadSuggestions();
      }

      // Load friends for suggestions
      const friendsResponse = await friendsAPI.getFriends();
      setFriends(friendsResponse.data);
    } catch (error) {
      console.error('Failed to load privacy data:', error);
    } finally {
      setSettingsLoading(false);
    }
  };

  const loadTrustedCircle = async () => {
    try {
      setTrustedCircleLoading(true);
      const response = await personaPrivacyAPI.getTrustedCircle();
      setTrustedCircle(response.data);
    } catch (error) {
      console.error('Failed to load trusted circle:', error);
    } finally {
      setTrustedCircleLoading(false);
    }
  };

  const loadOverrides = async () => {
    try {
      setOverridesLoading(true);
      const response = await personaPrivacyAPI.getOverrides();
      setOverrides(response.data);
    } catch (error) {
      console.error('Failed to load overrides:', error);
    } finally {
      setOverridesLoading(false);
    }
  };

  const loadSuggestions = async () => {
    try {
      setSuggestionsLoading(true);
      const [received, sent] = await Promise.all([
        personaPrivacyAPI.getReceivedSuggestions(),
        personaPrivacyAPI.getSentSuggestions()
      ]);
      setReceivedSuggestions(received.data);
      setSentSuggestions(sent.data);
    } catch (error) {
      console.error('Failed to load suggestions:', error);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  const handleUpdateSettings = async (updates) => {
    try {
      const response = await personaPrivacyAPI.updateSettings(updates);
      setPrivacySettings(prev => ({ ...prev, ...response.data }));

      // Reload Pro features if settings changed
      if (updates.trusted_circle_mode !== undefined) {
        await loadTrustedCircle();
        await loadOverrides();
      }
    } catch (error) {
      alert('Failed to update privacy settings: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleAddToTrustedCircle = async () => {
    if (!selectedFriend) return;

    try {
      await personaPrivacyAPI.addToTrustedCircle(selectedFriend.id);
      setShowAddTrusted(false);
      setSelectedFriend(null);
      await loadTrustedCircle();
    } catch (error) {
      alert('Failed to add to trusted circle: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleRemoveFromTrustedCircle = async (friendId) => {
    try {
      await personaPrivacyAPI.removeFromTrustedCircle(friendId);
      await loadTrustedCircle();
    } catch (error) {
      alert('Failed to remove from trusted circle: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleUpdateOverride = async () => {
    if (!selectedFriend) return;

    try {
      await personaPrivacyAPI.updateOverride(selectedFriend.id, overrideForm);
      setShowOverrideModal(false);
      setSelectedFriend(null);
      setOverrideForm({
        can_suggest: true,
        is_trusted: false,
        override_mode: null
      });
      await loadOverrides();
    } catch (error) {
      alert('Failed to update override: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleCreateSuggestion = async () => {
    if (!selectedPersona || !suggestionForm.tag.trim()) return;

    try {
      await personaPrivacyAPI.createSuggestion({
        persona_id: selectedPersona.id,
        category: suggestionForm.category,
        tag: suggestionForm.tag,
        weight: suggestionForm.weight,
        reasoning: suggestionForm.reasoning
      });

      setShowSuggestionModal(false);
      setSelectedPersona(null);
      setSuggestionForm({
        category: 'Technology',
        tag: '',
        weight: 0,
        reasoning: ''
      });
      await loadSuggestions();
      alert('Suggestion submitted successfully!');
    } catch (error) {
      alert('Failed to create suggestion: ' + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const handleReviewSuggestion = async (suggestionId, action) => {
    try {
      await personaPrivacyAPI.reviewSuggestion(suggestionId, action);
      await loadSuggestions();
    } catch (error) {
      alert(`Failed to ${action} suggestion: ` + (error.response?.data?.error || 'Unknown error'));
    }
  };

  const getWeightColor = (weight) => {
    if (weight >= 3) return 'teal';
    if (weight >= 1) return 'green';
    if (weight <= -3) return 'red';
    if (weight <= -1) return 'orange';
    return 'gray';
  };

  const getWeightText = (weight) => {
    if (weight > 0) return `+${weight}`;
    return weight === 0 ? '±0' : weight.toString();
  };

  if (settingsLoading) {
    return (
      <Center h="70vh">
        <Stack align="center" gap="md">
          <Loader size="xl" type="dots" />
          <Text c="dimmed" size="lg">Loading privacy settings...</Text>
        </Stack>
      </Center>
    );
  }

  const breadcrumbItems = [
    { title: 'Account', href: '/account' },
    { title: 'Persona Privacy', href: '#' }
  ].map((item, index) => (
    <Anchor key={index} href={item.href} onClick={(e) => { e.preventDefault(); navigate(item.href); }}>
      {item.title}
    </Anchor>
  ));

  return (
    <Box style={{ background: 'linear-gradient(180deg, rgba(25,113,194,0.03) 0%, rgba(0,0,0,0) 100%)' }}>
      <Container size="xl" py="xl" px="md">
        {/* Breadcrumbs */}
        <Box mb="lg">
          <Breadcrumbs>{breadcrumbItems}</Breadcrumbs>
        </Box>

        {/* Header */}
        <Box mb="xl" py="lg">
          <Group justify="space-between" align="flex-start">
            <Box>
              <Title
                order={1}
                size="h1"
                fw={700}
                style={{
                  background: 'linear-gradient(45deg, #1971c2 0%, #15aabf 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  marginBottom: '8px'
                }}
              >
                🔒 Persona Privacy
              </Title>
              <Text size="xl" c="dimmed" fw={400}>
                Control who can suggest traits for your personas
              </Text>
            </Box>
            <Button
              size="lg"
              variant="outline"
              onClick={() => navigate('/account')}
              style={{ boxShadow: '0 4px 12px rgba(25,113,194,0.1)' }}
            >
              ← Back to Account
            </Button>
          </Group>
        </Box>

        <Tabs defaultValue="settings">
          <Tabs.List mb="lg">
            <Tabs.Tab value="settings">Privacy Settings</Tabs.Tab>
            <Tabs.Tab value="trusted-circle" disabled={!privacySettings.is_pro_user}>
              Trusted Circle {privacySettings.is_pro_user ? '' : '(Pro)'}
            </Tabs.Tab>
            <Tabs.Tab value="overrides" disabled={!privacySettings.is_pro_user}>
              Friend Overrides {privacySettings.is_pro_user ? '' : '(Pro)'}
            </Tabs.Tab>
            <Tabs.Tab value="suggestions">Trait Suggestions</Tabs.Tab>
          </Tabs.List>

          {/* Privacy Settings Tab */}
          <Tabs.Panel value="settings">
            <Card p="lg" radius="lg" withBorder shadow="sm">
              <Stack gap="lg">
                <Box>
                  <Title order={3} mb="md">Global Privacy Mode</Title>
                  <Text size="sm" c="dimmed" mb="md">
                    Choose how friends can interact with your personas globally.
                    {privacySettings.is_pro_user ? ' Pro users can also enable Trusted Circle mode.' : ' Upgrade to Pro for more options.'}
                  </Text>

                  <Group grow>
                    <Card
                      p="md"
                      radius="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        border: privacySettings.privacy_mode === 'private' ? '2px solid #1971c2' : '1px solid #e9ecef'
                      }}
                      onClick={() => handleUpdateSettings({ privacy_mode: 'private' })}
                    >
                      <Group align="center" gap="sm">
                        <ThemeIcon size="lg" radius="xl" color="red" variant="light">
                          🚫
                        </ThemeIcon>
                        <Box>
                          <Text fw={600}>Private</Text>
                          <Text size="sm" c="dimmed">No one can suggest traits</Text>
                        </Box>
                      </Group>
                    </Card>

                    <Card
                      p="md"
                      radius="md"
                      withBorder
                      style={{
                        cursor: 'pointer',
                        border: privacySettings.privacy_mode === 'suggest-only' ? '2px solid #1971c2' : '1px solid #e9ecef'
                      }}
                      onClick={() => handleUpdateSettings({ privacy_mode: 'suggest-only' })}
                    >
                      <Group align="center" gap="sm">
                        <ThemeIcon size="lg" radius="xl" color="blue" variant="light">
                          💬
                        </ThemeIcon>
                        <Box>
                          <Text fw={600}>Suggest-Only</Text>
                          <Text size="sm" c="dimmed">Friends can suggest, you approve</Text>
                        </Box>
                      </Group>
                    </Card>

                    {privacySettings.is_pro_user && (
                      <Card
                        p="md"
                        radius="md"
                        withBorder
                        style={{
                          cursor: 'pointer',
                          border: privacySettings.trusted_circle_mode ? '2px solid #1971c2' : '1px solid #e9ecef'
                        }}
                        onClick={() => handleUpdateSettings({ trusted_circle_mode: !privacySettings.trusted_circle_mode })}
                      >
                        <Group align="center" gap="sm">
                          <ThemeIcon size="lg" radius="xl" color="teal" variant="light">
                            ⭐
                          </ThemeIcon>
                          <Box>
                            <Text fw={600}>Trusted Circle</Text>
                            <Text size="sm" c="dimmed">Auto-approve from trusted friends</Text>
                          </Box>
                        </Group>
                      </Card>
                    )}
                  </Group>
                </Box>

                <Divider />

                <Box>
                  <Title order={3} mb="md">Usage Statistics</Title>
                  <Group gap="xl">
                    <Box>
                      <Text size="sm" c="dimmed">Suggestions This Month</Text>
                      <Text size="xl" fw={700}>{privacySettings.suggestions_this_month}</Text>
                    </Box>
                    <Box>
                      <Text size="sm" c="dimmed">Monthly Limit</Text>
                      <Text size="xl" fw={700}>
                        {privacySettings.is_pro_user ? 'Unlimited' : privacySettings.suggestions_limit}
                      </Text>
                    </Box>
                  </Group>
                </Box>
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Trusted Circle Tab (Pro) */}
          <Tabs.Panel value="trusted-circle">
            <Card p="lg" radius="lg" withBorder shadow="sm">
              <Stack gap="lg">
                <Group justify="space-between">
                  <Box>
                    <Title order={3}>Trusted Circle</Title>
                    <Text size="sm" c="dimmed">
                      Friends in your trusted circle can have their suggestions auto-approved when Trusted Circle mode is enabled.
                    </Text>
                  </Box>
                  <Button onClick={() => setShowAddTrusted(true)}>Add Friend</Button>
                </Group>

                {trustedCircleLoading ? (
                  <Center py="lg">
                    <Loader />
                  </Center>
                ) : trustedCircle.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No friends in your trusted circle yet. Add trusted friends to enable auto-approval of their suggestions.
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {trustedCircle.map(friend => (
                      <Card key={friend.id} p="sm" radius="md" withBorder>
                        <Group justify="space-between">
                          <Group gap="sm">
                            <Avatar size="sm" radius="xl" />
                            <Box>
                              <Text size="sm" fw={600}>{friend.name}</Text>
                              <Text size="xs" c="dimmed">{friend.email}</Text>
                            </Box>
                          </Group>
                          <Group gap="xs">
                            <Badge size="sm" color="teal">Trusted</Badge>
                            <ActionIcon
                              size="sm"
                              color="red"
                              variant="subtle"
                              onClick={() => handleRemoveFromTrustedCircle(friend.id)}
                            >
                              ✕
                            </ActionIcon>
                          </Group>
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Friend Overrides Tab (Pro) */}
          <Tabs.Panel value="overrides">
            <Card p="lg" radius="lg" withBorder shadow="sm">
              <Stack gap="lg">
                <Box>
                  <Title order={3} mb="md">Per-Friend Overrides</Title>
                  <Text size="sm" c="dimmed">
                    Set individual privacy settings for specific friends to override your global mode.
                  </Text>
                </Box>

                {overridesLoading ? (
                  <Center py="lg">
                    <Loader />
                  </Center>
                ) : overrides.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No friend-specific overrides set. All friends follow your global privacy mode.
                  </Text>
                ) : (
                  <Table>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Friend</Table.Th>
                        <Table.Th>Can Suggest</Table.Th>
                        <Table.Th>Is Trusted</Table.Th>
                        <Table.Th>Override Mode</Table.Th>
                        <Table.Th>Actions</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {overrides.map(override => (
                        <Table.Tr key={override.friend_id}>
                          <Table.Td>
                            <Group gap="sm">
                              <Avatar size="sm" radius="xl" />
                              <Box>
                                <Text size="sm" fw={600}>{override.name}</Text>
                                <Text size="xs" c="dimmed">{override.email}</Text>
                              </Box>
                            </Group>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={override.can_suggest ? 'green' : 'red'}>
                              {override.can_suggest ? 'Yes' : 'No'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            <Badge color={override.is_trusted ? 'teal' : 'gray'}>
                              {override.is_trusted ? 'Yes' : 'No'}
                            </Badge>
                          </Table.Td>
                          <Table.Td>
                            {override.override_mode ? (
                              <Badge variant="outline">{override.override_mode}</Badge>
                            ) : (
                              <Text size="sm" c="dimmed">-</Text>
                            )}
                          </Table.Td>
                          <Table.Td>
                            <Button
                              size="xs"
                              variant="light"
                              onClick={() => {
                                setSelectedFriend(override);
                                setOverrideForm({
                                  can_suggest: override.can_suggest,
                                  is_trusted: override.is_trusted,
                                  override_mode: override.override_mode
                                });
                                setShowOverrideModal(true);
                              }}
                            >
                              Edit
                            </Button>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                )}

                <Button variant="outline" onClick={() => setShowOverrideModal(true)}>
                  Set Override for Friend
                </Button>
              </Stack>
            </Card>
          </Tabs.Panel>

          {/* Suggestions Tab */}
          <Tabs.Panel value="suggestions">
            <Tabs defaultValue="received">
              <Tabs.List mb="md">
                <Tabs.Tab value="received">Received ({receivedSuggestions.length})</Tabs.Tab>
                <Tabs.Tab value="sent">Sent ({sentSuggestions.length})</Tabs.Tab>
                <Tabs.Tab value="create">Create Suggestion</Tabs.Tab>
              </Tabs.List>

              <Tabs.Panel value="received">
                {suggestionsLoading ? (
                  <Center py="lg">
                    <Loader />
                  </Center>
                ) : receivedSuggestions.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No suggestions received yet.
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {receivedSuggestions.map(suggestion => (
                      <Card key={suggestion.id} p="md" radius="md" withBorder>
                        <Group justify="space-between" mb="xs">
                          <Group gap="sm">
                            <Badge size="sm" variant="light">{suggestion.category}</Badge>
                            <Text fw={600}>{suggestion.tag}</Text>
                            <Badge color={getWeightColor(suggestion.weight)} size="sm">
                              {getWeightText(suggestion.weight)}
                            </Badge>
                          </Group>
                          <Group gap="xs">
                            {suggestion.status === 'pending' && (
                              <>
                                <Button size="xs" color="teal" onClick={() => handleReviewSuggestion(suggestion.id, 'approve')}>
                                  ✓ Approve
                                </Button>
                                <Button size="xs" color="red" variant="outline" onClick={() => handleReviewSuggestion(suggestion.id, 'decline')}>
                                  ✕ Decline
                                </Button>
                              </>
                            )}
                            <Badge color={
                              suggestion.status === 'approved' ? 'teal' :
                              suggestion.status === 'declined' ? 'red' :
                              suggestion.status === 'auto-approved' ? 'green' : 'orange'
                            }>
                              {suggestion.status}
                            </Badge>
                          </Group>
                        </Group>

                        <Text size="sm" mb="xs">{suggestion.reasoning}</Text>

                        <Group gap="sm">
                          <Text size="xs" c="dimmed">
                            From: {suggestion.suggested_by_name} • {suggestion.persona_name}
                          </Text>
                          {suggestion.is_from_trusted_circle && (
                            <Badge size="xs" color="teal" variant="dot">Trusted Circle</Badge>
                          )}
                        </Group>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="sent">
                {suggestionsLoading ? (
                  <Center py="lg">
                    <Loader />
                  </Center>
                ) : sentSuggestions.length === 0 ? (
                  <Text c="dimmed" ta="center" py="lg">
                    No suggestions sent yet.
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {sentSuggestions.map(suggestion => (
                      <Card key={suggestion.id} p="md" radius="md" withBorder>
                        <Group justify="space-between" mb="xs">
                          <Group gap="sm">
                            <Badge size="sm" variant="light">{suggestion.category}</Badge>
                            <Text fw={600}>{suggestion.tag}</Text>
                            <Badge color={getWeightColor(suggestion.weight)} size="sm">
                              {getWeightText(suggestion.weight)}
                            </Badge>
                          </Group>
                          <Badge color={
                            suggestion.status === 'approved' ? 'teal' :
                            suggestion.status === 'declined' ? 'red' :
                            suggestion.status === 'auto-approved' ? 'green' : 'orange'
                          }>
                            {suggestion.status}
                          </Badge>
                        </Group>

                        <Text size="sm" mb="xs">{suggestion.reasoning}</Text>

                        <Text size="xs" c="dimmed">
                          To: {suggestion.persona_owner_name} • {suggestion.persona_name}
                        </Text>
                      </Card>
                    ))}
                  </Stack>
                )}
              </Tabs.Panel>

              <Tabs.Panel value="create">
                <Card p="lg" radius="lg" withBorder shadow="sm">
                  <Stack gap="lg">
                    <Box>
                      <Title order={3} mb="md">Suggest a Trait</Title>
                      <Text size="sm" c="dimmed">
                        Suggest a preference or trait for one of your friend's personas.
                      </Text>
                    </Box>

                    <Select
                      label="Friend's Persona"
                      placeholder="Choose a friend's persona"
                      data={friends.flatMap(friend =>
                        friend.personas?.map(persona => ({
                          value: persona.id.toString(),
                          label: `${friend.name}: ${persona.name}`
                        })) || []
                      )}
                      value={selectedPersona?.id?.toString() || ''}
                      onChange={(value) => {
                        const persona = friends.flatMap(f => f.personas || [])
                          .find(p => p.id.toString() === value);
                        setSelectedPersona(persona);
                      }}
                    />

                    <Group grow>
                      <Select
                        label="Category"
                        data={categories}
                        value={suggestionForm.category}
                        onChange={(value) => setSuggestionForm({ ...suggestionForm, category: value })}
                      />
                      <TextInput
                        label="Trait/Preference"
                        placeholder="e.g., coffee, gaming"
                        value={suggestionForm.tag}
                        onChange={(e) => setSuggestionForm({ ...suggestionForm, tag: e.target.value })}
                      />
                    </Group>

                    <Select
                      label="Importance Level"
                      data={Array.from({length: 11}, (_, i) => {
                        const val = i - 5;
                        return { value: val.toString(), label: val > 0 ? `+${val}` : val.toString() };
                      })}
                      value={suggestionForm.weight.toString()}
                      onChange={(value) => setSuggestionForm({ ...suggestionForm, weight: parseInt(value) })}
                    />

                    <Textarea
                      label="Reasoning (optional)"
                      placeholder="Why do you think this person would like this?"
                      value={suggestionForm.reasoning}
                      onChange={(e) => setSuggestionForm({ ...suggestionForm, reasoning: e.target.value })}
                      rows={3}
                    />

                    <Group grow>
                      <Button onClick={handleCreateSuggestion} disabled={!selectedPersona || !suggestionForm.tag.trim()}>
                        Send Suggestion
                      </Button>
                      <Button variant="outline" onClick={() => {
                        setSelectedPersona(null);
                        setSuggestionForm({
                          category: 'Technology',
                          tag: '',
                          weight: 0,
                          reasoning: ''
                        });
                      }}>
                        Clear
                      </Button>
                    </Group>
                  </Stack>
                </Card>
              </Tabs.Panel>
            </Tabs>
          </Tabs.Panel>
        </Tabs>

        {/* Add to Trusted Circle Modal */}
        <Modal
          opened={showAddTrusted}
          onClose={() => setShowAddTrusted(false)}
          title="Add Friend to Trusted Circle"
          size="md"
        >
          <Stack gap="md">
            <Select
              label="Select Friend"
              placeholder="Choose a friend to add"
              data={friends.map(friend => ({
                value: friend.id.toString(),
                label: friend.name
              }))}
              value={selectedFriend?.id?.toString() || ''}
              onChange={(value) => {
                const friend = friends.find(f => f.id.toString() === value);
                setSelectedFriend(friend);
              }}
            />
            <Group grow>
              <Button onClick={handleAddToTrustedCircle} disabled={!selectedFriend}>
                Add to Trusted Circle
              </Button>
              <Button variant="outline" onClick={() => setShowAddTrusted(false)}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>

        {/* Friend Override Modal */}
        <Modal
          opened={showOverrideModal}
          onClose={() => setShowOverrideModal(false)}
          title={selectedFriend ? `Override for ${selectedFriend.name}` : "Set Friend Override"}
          size="md"
        >
          <Stack gap="md">
            {!selectedFriend && (
              <Select
                label="Select Friend"
                placeholder="Choose a friend"
                data={friends.map(friend => ({
                  value: friend.id.toString(),
                  label: friend.name
                }))}
                value={selectedFriend?.id?.toString() || ''}
                onChange={(value) => {
                  const friend = friends.find(f => f.id.toString() === value);
                  setSelectedFriend(friend);
                }}
              />
            )}

            <Switch
              label="Can suggest traits"
              checked={overrideForm.can_suggest}
              onChange={(e) => setOverrideForm({ ...overrideForm, can_suggest: e.currentTarget.checked })}
            />

            <Switch
              label="Is trusted (auto-approve suggestions)"
              checked={overrideForm.is_trusted}
              onChange={(e) => setOverrideForm({ ...overrideForm, is_trusted: e.currentTarget.checked })}
            />

            <Select
              label="Override Privacy Mode (optional)"
              placeholder="Leave empty to use global mode"
              data={[
                { value: 'private', label: 'Private - No suggestions' },
                { value: 'suggest-only', label: 'Suggest-Only - Manual approval' },
                { value: 'trusted-circle', label: 'Trusted Circle - Auto-approve' }
              ]}
              value={overrideForm.override_mode || ''}
              onChange={(value) => setOverrideForm({ ...overrideForm, override_mode: value })}
              clearable
            />

            <Group grow>
              <Button onClick={handleUpdateOverride} disabled={!selectedFriend}>
                {selectedFriend ? 'Update Override' : 'Set Override'}
              </Button>
              <Button variant="outline" onClick={() => setShowOverrideModal(false)}>
                Cancel
              </Button>
            </Group>
          </Stack>
        </Modal>
      </Container>
    </Box>
  );
};

export default PersonaPrivacyPage;
